import type { ReactNode } from 'react'

import {
  Card,
  DataField,
  Header,
  IconQuestion,
  IconShow,
  SecondaryButton,
  Space,
  StatusBadge,
  WithPopover,
} from '@procurify/ui'

import { CostSummary } from '@webapp/core/components/CostSummary'
import { Price } from '@webapp/core/components/Price'
import { useProcurifyIntl } from '@webapp/hooks'
import { FinancialAccountSummaryLoader } from './components/FinancialAccountSummaryLoader/FinancialAccountSummaryLoader'
import messages from './messages'
import { FinancialAccountSummaryCardStyled } from './styles/FinancialAccountSummaryCardStyled'
import { useFinancialAccountSummaryCard } from './useFinancialAccountSummaryCard'

export interface IFinancialAccountSummaryCardProps {
  actions?: ReactNode
}

export const FinancialAccountSummaryCard = ({
  actions,
}: IFinancialAccountSummaryCardProps) => {
  const intl = useProcurifyIntl()

  const {
    data: {
      currency,
      icon,
      accountBalance,
      allocatedFunds,
      allocatedToBillPayments,
      allocatedToSpendingCards,
      availableFunds,
      availableFundsTooltipText,
      flagLabel,
    },
    commands: { revealBalanceSummary },
    states: {
      showBalanceSummary,
      showInsufficientFundsLabel,
      showLoader,
      showInactiveAccountLabel,
      showBillPaymentsAllocatedFunds,
      showSpendingCardsAllocatedFunds,
      showShowBalanceButton,
      isShowBalanceButtonLoading,
    },
  } = useFinancialAccountSummaryCard()

  return (
    <FinancialAccountSummaryCardStyled>
      <Card>
        <Card.Content>
          {showLoader ? (
            <FinancialAccountSummaryLoader />
          ) : (
            <Space
              direction='horizontal'
              align='center'
              justify='space-between'
            >
              <Space direction='horizontal' size='xlarge'>
                <Space direction='horizontal' align='center'>
                  <img src={icon} alt={flagLabel} height={32} />

                  <DataField
                    label={intl.formatMessage(messages.accountBalanceLabel)}
                  >
                    {showInactiveAccountLabel ? (
                      <StatusBadge
                        type='warning'
                        text={intl.formatMessage(messages.inactiveAccountLabel)}
                      />
                    ) : (
                      <Space direction='horizontal' size='small' align='end'>
                        <Header as='h5'>
                          {showBalanceSummary ? (
                            <Price price={accountBalance} currency={currency} />
                          ) : (
                            '••••••••'
                          )}
                        </Header>
                        {showInsufficientFundsLabel && (
                          <StatusBadge
                            text={intl.formatMessage(
                              messages.insufficientFundsLabel
                            )}
                            type='warning'
                          />
                        )}
                      </Space>
                    )}
                  </DataField>
                </Space>

                <Space.Divider />

                <WithPopover
                  isDisabled={!showBalanceSummary}
                  content={
                    <Card.Content>
                      <CostSummary>
                        <CostSummary.Cost>
                          {showSpendingCardsAllocatedFunds && (
                            <>
                              <th>
                                {intl.formatMessage(
                                  messages.spendingCardsLabel
                                )}
                              </th>
                              <td>
                                <Price
                                  currency={currency}
                                  price={allocatedToSpendingCards}
                                />
                              </td>
                            </>
                          )}

                          {showBillPaymentsAllocatedFunds && (
                            <>
                              <th>
                                {intl.formatMessage(
                                  messages.upcomingPaymentsLabel
                                )}
                              </th>
                              <td>
                                <Price
                                  currency={currency}
                                  price={allocatedToBillPayments}
                                />
                              </td>
                            </>
                          )}
                        </CostSummary.Cost>
                      </CostSummary>
                    </Card.Content>
                  }
                  placement='top'
                >
                  <DataField
                    label={intl.formatMessage(messages.allocatedFundsLabel)}
                    labelIcon={<IconQuestion />}
                  >
                    <Header as='h5'>
                      {showBalanceSummary ? (
                        <Price price={allocatedFunds} currency={currency} />
                      ) : (
                        '••••••••'
                      )}
                    </Header>
                  </DataField>
                </WithPopover>

                <Space.Divider />

                <DataField
                  label={intl.formatMessage(messages.availableFundsLabel)}
                  labelTooltipMessage={availableFundsTooltipText}
                  labelTooltipAriaLabel={intl.formatMessage(
                    messages.availableFundsTooltipLabel
                  )}
                  labelIcon={<IconQuestion />}
                >
                  <Header as='h5'>
                    {showBalanceSummary ? (
                      <Price price={availableFunds} currency={currency} />
                    ) : (
                      '••••••••'
                    )}
                  </Header>
                </DataField>
              </Space>

              <Space direction='horizontal'>
                {showShowBalanceButton && (
                  <SecondaryButton
                    iconComponent={<IconShow />}
                    onClick={revealBalanceSummary}
                    disabled={isShowBalanceButtonLoading}
                  >
                    {intl.formatMessage(messages.showBalanceButtonText)}
                  </SecondaryButton>
                )}

                {actions}
              </Space>
            </Space>
          )}
        </Card.Content>
      </Card>
    </FinancialAccountSummaryCardStyled>
  )
}
